import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, Loader2, Lock, Building2 } from 'lucide-react';
import { api } from '../../services/api';
import type { Contact } from '../../types';
import { toast } from '../../store/toastStore';
import Badge from '../ui/Badge';
import Dialog from '../ui/Dialog';
import { CONTACT_ORG_ID, CONTACT_SCHEMA, CONTACT_TYPE_FIRST_ID, accessFieldId, contactFormProblems, focusFirstProblem, problemOn } from './portalCustomerStaffModel';
import SaveProblemsSummary from './SaveProblemsSummary';
import Field, { FormServerError, useFormValidation } from '../ui/Field';
import ForwardLink from '../details/ForwardLink';
import {
  ACCESS_LEVEL_HINT_KEY, CONTACT_TYPES, CONTACT_TYPE_LABEL_KEY, accessBlocker, contactCreatePayload, contactServerFieldIssue, trimContactText,
  defaultAccessLevel, duplicateContactFromError, followEmail, grantLevel, isContactType, type ContactType,
} from './contactAccessModel';
import {
  CAPPED_PERM_KEYS, NO_CAP, PERM_LABEL_KEY, isManagerLevel, levelDefaultPerms, permsPayload, seesAllTickets,
  type PermCeiling, type PermDraft,
} from './portalPermsModel';
import { reportScheduledMail } from '../../utils/scheduledMail';
import EntityPicker from '../ui/EntityPicker';

/**
 * P-18 — THE contact form (D-135: "אותו טופס בדיוק" in three places — the
 * Contacts page, the Contacts tab of a customer and "גישה חדשה" on the
 * Portal-access page) and its building blocks.
 */

/* ───────────────────────── access fields (the shared block) ───────────────────────── */

export interface AccessDraft {
  email: string;
  level: ContactType;
  approveTickets: boolean;
  password: string;
  /** P-21 (D-152): the access checkboxes. Present = the block shows them. */
  perms?: PermDraft;
}

/**
 * P-21 (D-152): what the caller may tick (the GM: everything; an Account
 * Manager: what they see themselves). Until it is known, nothing is capped in
 * the UI — the server enforces the ceiling either way.
 */
export function usePermCeiling(enabled = true): PermCeiling {
  const { data } = useQuery({
    queryKey: ['portalAccessCeiling'],
    queryFn: () => api.getPortalAccessCeiling(),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
  return data?.data?.ceiling ?? NO_CAP;
}

/**
 * P-21 (D-152) — the customer user's checkboxes: their own tickets (always,
 * locked), all of the company's tickets (D-158 QX-86: `view_all_tickets`,
 * a real box; ticked and locked while "מאשר קריאות" is on), projects, documents, hours, billing
 * and "מאשר קריאות". A box above the caller's ceiling is disabled with a note.
 */
export function PortalPermissionCheckboxes({ idPrefix, perms, onPerms, approveTickets, onApproveTickets, managerRank, ceiling = NO_CAP, disabled = false }: {
  idPrefix: string;
  perms: PermDraft;
  onPerms: (next: PermDraft) => void;
  approveTickets: boolean;
  onApproveTickets: (v: boolean) => void;
  /** The level is "מנהל מטעם הלקוח" / "איש קשר ראשי". */
  managerRank: boolean;
  ceiling?: PermCeiling;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const row = 'flex items-start gap-2 text-sm text-surface-800';
  const box = 'h-4 w-4 mt-0.5 rounded accent-primary-700';
  const allTickets = seesAllTickets(perms.view_all_tickets, approveTickets);
  void managerRank; // the level's default is in `perms` already (levelDefaultPerms)
  return (
    <fieldset disabled={disabled} className="space-y-2">
      <legend className="label">{t('portalAccess.permsLegend')}</legend>
      <label className={`${row} opacity-80`}>
        <input type="checkbox" checked disabled readOnly className={box} />
        <span>
          <span className="block">{t('portalAccess.perm.ownTickets')}</span>
          <span className="block text-xs text-surface-600">{t('portalAccess.perm.alwaysHint')}</span>
        </span>
      </label>
      <label htmlFor={`${idPrefix}-perm-all-tickets`} className={`${row} ${approveTickets ? 'opacity-80' : 'cursor-pointer'}`}>
        <input id={`${idPrefix}-perm-all-tickets`} type="checkbox" checked={allTickets} disabled={approveTickets} className={box}
          onChange={(e) => onPerms({ ...perms, view_all_tickets: e.target.checked })} />
        <span>
          <span className="block">{t('portalAccess.perm.allTickets')}</span>
          <span className="block text-xs text-surface-600">{t(approveTickets ? 'portalAccess.perm.allTicketsApproverHint' : 'portalAccess.perm.allTicketsHint')}</span>
        </span>
      </label>
      {CAPPED_PERM_KEYS.map((k) => {
        const capped = !ceiling[k];
        const id = `${idPrefix}-perm-${k}`;
        return (
          <label key={k} htmlFor={id} className={`${row} ${capped ? 'opacity-60' : 'cursor-pointer'}`}>
            <input id={id} type="checkbox" className={box} checked={perms[k]} disabled={capped}
              aria-describedby={capped ? `${id}-cap` : undefined}
              onChange={(e) => onPerms({ ...perms, [k]: e.target.checked })} />
            <span>
              <span className="block">{t(PERM_LABEL_KEY[k])}</span>
              {capped && (
                <span id={`${id}-cap`} className="flex items-center gap-1 text-xs text-surface-600">
                  <Lock size={12} aria-hidden="true" />{t('portalAccess.perm.cappedHint')}
                </span>
              )}
            </span>
          </label>
        );
      })}
      <label className={`${row} cursor-pointer`}>
        <input type="checkbox" checked={approveTickets} onChange={(e) => onApproveTickets(e.target.checked)} className={box} />
        <span>
          <span className="block">{t('portalAccess.approveTickets')}</span>
          <span className="block text-xs text-surface-600">{t('portalAccess.approveTicketsHint')}</span>
        </span>
      </label>
    </fieldset>
  );
}

/**
 * The portal-access block: e-mail (filled from the contact, editable), the
 * access level (pre-selected from the contact's type — D-135), "מאשר
 * קריאות" and the password. Used by the contact form and by the "existing
 * contact" track of the Portal-access page.
 */
export function PortalAccessFields({ idPrefix, value, onChange, error, disabled = false, typeDefault, onEmailBlur, emailError, emailNotice, levelError, passwordError }: {
  idPrefix: string;
  value: AccessDraft;
  onChange: (patch: Partial<AccessDraft>) => void;
  error?: string;
  disabled?: boolean;
  /** D-147 (QX-78): the contact's type — a different level shows a note. */
  typeDefault?: ContactType | '' | null;
  /** D-230 s.11 - check the login e-mail when leaving the field. */
  onEmailBlur?: () => void;
  emailError?: string;
  /** Shown right under the e-mail field (the "already in use" actions). */
  emailNotice?: ReactNode;
  /** W-08c: a refused save - the level / password message, the field marked red. */
  levelError?: string;
  passwordError?: string;
}) {
  const { t } = useTranslation();
  // P-21 (D-152): with checkboxes, a level change (and the ceiling arriving)
  // resets them to that level's defaults — the manager: everything allowed.
  const withPerms = !!value.perms;
  const ceiling = usePermCeiling(withPerms);
  const ceilingKey = CAPPED_PERM_KEYS.map((k) => (ceiling[k] ? 1 : 0)).join('');
  useEffect(() => {
    if (withPerms) onChange({ perms: levelDefaultPerms(value.level, ceiling) });
  }, [value.level, ceilingKey, withPerms]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-3">
      <Field id={`${idPrefix}-access-email`} label={t('contacts.accessEmail')} required hint={t('contacts.accessEmailHint')} error={emailError}>
        <input type="email" dir="ltr" spellCheck={false} autoComplete="off" value={value.email} disabled={disabled}
          onChange={(e) => onChange({ email: e.target.value })} onBlur={onEmailBlur} className="input text-start" />
      </Field>
      {emailNotice}
      <fieldset disabled={disabled}>
        <legend className="label">
          {t('contacts.accessType')} <span className="text-danger-700" aria-hidden="true">*</span>
        </legend>
        <div className="space-y-2">
          {CONTACT_TYPES.map((lvl, i) => (
            <label key={lvl} className="flex items-start gap-2 text-sm text-surface-800 cursor-pointer">
              <input type="radio" name={`${idPrefix}-access-level`} className="h-4 w-4 mt-0.5 accent-primary-700"
                id={i === 0 ? accessFieldId(idPrefix, 'level') : undefined} aria-invalid={(i === 0 && !!levelError) || undefined}
                checked={value.level === lvl} onChange={() => onChange({ level: lvl })} />
              <span>
                <span className="block">{t(CONTACT_TYPE_LABEL_KEY[lvl])}</span>
                <span className="block text-xs text-surface-600">{t(ACCESS_LEVEL_HINT_KEY[lvl])}</span>
              </span>
            </label>
          ))}
        </div>
        {isContactType(typeDefault) && value.level !== typeDefault && (
          <p className="mt-1 text-xs text-warning-800" role="status">{t('contacts.levelDiffersFromType')}</p>
        )}
        {levelError && <p className="error-text">{levelError}</p>}
      </fieldset>
      {value.perms ? (
        <PortalPermissionCheckboxes idPrefix={idPrefix} perms={value.perms} onPerms={(perms) => onChange({ perms })}
          approveTickets={value.approveTickets} onApproveTickets={(v) => onChange({ approveTickets: v })}
          managerRank={isManagerLevel(value.level)} ceiling={ceiling} disabled={disabled} />
      ) : (
        <label className="flex items-start gap-2 text-sm text-surface-800 cursor-pointer">
          <input type="checkbox" checked={value.approveTickets} disabled={disabled}
            onChange={(e) => onChange({ approveTickets: e.target.checked })} className="h-4 w-4 mt-0.5 rounded accent-primary-700" />
          <span>
            <span className="block">{t('portalAccess.approveTickets')}</span>
            <span className="block text-xs text-surface-600">{t('portalAccess.approveTicketsHint')}</span>
          </span>
        </label>
      )}
      <Field id={`${idPrefix}-access-password`} label={t('contacts.password')} required hint={t('contacts.passwordMailedHint')} error={passwordError}>
        <input type="text" dir="ltr" autoComplete="new-password" value={value.password} disabled={disabled}
          onChange={(e) => onChange({ password: e.target.value })} className="input text-start" />
      </Field>
      {error && <p className="error-text" role="alert">{error}</p>}
    </div>
  );
}

/**
 * D-138 / D-141 — "יצירת גישת פורטל" on a NEW customer (customer form, lead
 * conversion): e-mail (filled from the customer's e-mail, editable) and a
 * password, with the explanation of what saving creates.
 */
export function CustomerPortalAccessFields({ idPrefix, enabled, onEnabled, email, onEmail, password, onPassword, error, onEmailBlur, emailError, emailNotice }: {
  idPrefix: string;
  enabled: boolean;
  onEnabled: (v: boolean) => void;
  email: string;
  onEmail: (v: string) => void;
  password: string;
  onPassword: (v: string) => void;
  error?: string;
  onEmailBlur?: () => void;
  emailError?: string;
  emailNotice?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-surface-200 bg-surface-50 p-3 space-y-3">
      <label className="flex items-center gap-2 text-sm font-medium text-surface-900">
        <input type="checkbox" checked={enabled} onChange={(e) => onEnabled(e.target.checked)} className="h-4 w-4 rounded-xs accent-primary-700" />
        {t('contacts.grantPortalAccess')}
      </label>
      {enabled && (
        <>
          <p className="flex items-start gap-2 text-xs text-surface-700">
            <Info size={14} className="mt-0.5 flex-shrink-0 text-primary-700" aria-hidden="true" />
            <span>{t('contacts.customerAccessExplain')}</span>
          </p>
          <Field id={`${idPrefix}-access-email`} label={t('contacts.accessEmail')} required hint={t('contacts.customerAccessEmailHint')} error={emailError}>
            <input type="email" dir="ltr" spellCheck={false} autoComplete="off" value={email}
              onChange={(e) => onEmail(e.target.value)} onBlur={onEmailBlur} className="input text-start" />
          </Field>
          {emailNotice}
          <Field id={`${idPrefix}-access-password`} label={t('contacts.password')} required hint={t('contacts.passwordMailedHint')}>
            <input type="text" dir="ltr" autoComplete="new-password" value={password}
              onChange={(e) => onPassword(e.target.value)} className="input text-start" />
          </Field>
          {error && <p className="error-text" role="alert">{error}</p>}
        </>
      )}
    </div>
  );
}

/* ───────────────────────── access status (D-136) ───────────────────────── */

/**
 * D-136: the contact form SHOWS the access — status, level, approver — and
 * links to its record on the Portal-access page ("ניהול גישה"), where alone
 * it is edited. The link is the General Manager's (that page is theirs).
 */
export function ContactAccessStatus({ contact, canManageAccess, canCreateAccess = false }: {
  contact: Contact;
  /** The portal-access page link (the GM's screen). */
  canManageAccess: boolean;
  /** D-147 (QX-79): may create access for this contact (GM, or the AM of its customer). */
  canCreateAccess?: boolean;
}) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  const level = grantLevel(contact.portal_access_role, contact.contact_role);
  return (
    <div className="rounded-lg border border-surface-200 bg-surface-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5 text-sm">
          <span className="font-medium text-surface-900">{t('contacts.portalAccessSection')}:</span>
          {contact.portal_access_id ? (
            <>
              <Badge tone={contact.portal_access_active ? 'success' : 'neutral'} dot>
                {contact.portal_access_active ? t('contacts.accessActive') : t('contacts.accessInactive')}
              </Badge>
              {level && <Badge tone={level === 'customer_employee' ? 'info' : 'brand'}>{t(CONTACT_TYPE_LABEL_KEY[level])}</Badge>}
              {contact.portal_access_approve_tickets && <Badge tone="warning">{t('portalAccess.approveTickets')}</Badge>}
            </>
          ) : (
            <span className="text-surface-600">{t('contacts.noAccess')}</span>
          )}
        </div>
        {contact.portal_access_id ? (
          canManageAccess && (
            <ForwardLink to={`/portal-access?grant=${contact.portal_access_id}`}>{t('contacts.manageAccess')}</ForwardLink>
          )
        ) : canCreateAccess && contact.active ? (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCreating(true)}>{t('portalAccess.newGrant')}</button>
        ) : canManageAccess ? (
          <ForwardLink to={`/portal-access?contact=${contact.id}`}>{t('contacts.manageAccess')}</ForwardLink>
        ) : null}
      </div>
      {creating && <CreateContactAccessDialog contact={contact} onClose={() => setCreating(false)} />}
      {!contact.active && contact.portal_access_id && !contact.portal_access_active && (
        <p className="mt-2 text-xs text-surface-600">{t('contacts.restoreNoAccess')}</p>
      )}
    </div>
  );
}

/**
 * D-147 (QX-79) — "הענקת גישה חדשה" for an EXISTING contact, from the
 * contact: the same access block (e-mail from the contact, level from its
 * type). POST /api/contacts/:id/portal-access — the GM, or the Account
 * Manager of the contact's customer.
 */
export function CreateContactAccessDialog({ contact, onClose, onCreated }: {
  contact: Contact; onClose: () => void;
  /** P-42: the new grant (carries `user_id`), e.g. to add the contact to a discussion. */
  onCreated?: (grant: { user_id?: string | null } | null | undefined) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const typeDefault = isContactType(contact.contact_role) ? contact.contact_role : null;
  const [access, setAccess] = useState<AccessDraft>({
    email: contact.email || '', level: defaultAccessLevel(typeDefault), approveTickets: false, password: '',
    perms: levelDefaultPerms(defaultAccessLevel(typeDefault)),
  });
  const blockKey = accessBlocker({ email: access.email, level: access.level, password: access.password });
  const mutation = useMutation({
    mutationFn: () => api.createContactPortalAccess(contact.id, {
      access_type: access.level,
      password: access.password,
      approve_tickets: access.approveTickets,
      ...(access.perms ? { permissions: permsPayload(access.perms, access.approveTickets) } : {}),
      ...(access.email.trim() && access.email.trim() !== (contact.email || '') ? { email: access.email.trim() } : {}),
    }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['contact'] });
      queryClient.invalidateQueries({ queryKey: ['portalAccess'] });
      if (reportScheduledMail(res)) { /* P-27: queued - "בטל" toast */ }
      else if (res.email_sent === true) toast('success', t('portalAccess.emailSent'));
      else if (res.email_sent === false) toast('warning', t('portalAccess.emailFailed'));
      onCreated?.(res?.data as { user_id?: string | null } | undefined);
      onClose();
    },
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('portalAccess.newGrant')}
      description={contact.name}
      size="md"
      dismissible={!mutation.isPending}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary flex-1 sm:flex-none">{t('common.cancel')}</button>
          <button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending || !!blockKey}
            aria-busy={mutation.isPending || undefined} className="btn btn-primary flex-1 sm:flex-none">
            {mutation.isPending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {t('portalAccess.newGrant')}
          </button>
        </>
      )}
    >
      <div className="space-y-3">
        <PortalAccessFields idPrefix="contact-grant" value={access} typeDefault={typeDefault}
          onChange={(p) => setAccess((prev) => ({ ...prev, ...p }))} error={blockKey ? t(blockKey) : undefined} />
        <FormServerError error={mutation.error} fallback={t('common.saveError')} />
      </div>
    </Dialog>
  );
}

/* ───────────────────────── the contact form ───────────────────────── */

// CONTACT_SCHEMA (the blur rules) lives in portalCustomerStaffModel - the same rules decide a save (W-08c).

export interface ContactFormModalProps {
  /** null = create. */
  contact: Contact | null;
  /** The customers the caller may add contacts to (the select's options). */
  organizations: { id: string; name: string }[];
  /** A fixed customer (the customer page's tab): no select. */
  fixedOrgId?: string;
  /** W-08: a customer already chosen elsewhere (the portal-access window) — preselected, still changeable. */
  defaultOrgId?: string;
  /** May create portal access here (the GM; D-147: also the Account Manager, for their customers). */
  canGrantAccess: boolean;
  /** The portal-access page link ("ניהול גישה") — the GM's screen. Default: false. */
  canOpenAccessPage?: boolean;
  /** Portal-access page "גישה חדשה": the access block is on and cannot be turned off. */
  forceAccess?: boolean;
  /** Extra content above the fields (the Portal-access page's track choice). */
  headerSlot?: ReactNode;
  title?: string;
  /**
   * D-250 (portal-access "+ איש קשר חדש"): the server refuses a contact whose e-mail
   * already sits on an active contact of the same customer, and this callback lets the
   * user pick that contact instead. Without it the form behaves as before.
   */
  onPickExisting?: (contact: { id: string; organization_id: string }) => void;
  onClose: () => void;
  onSaved?: () => void;
}

/**
 * THE contact form (D-135). Create: the customer, the TYPE (one of three,
 * required), details, "איש קשר ראשי", and — for the General Manager — the
 * optional portal access, whose e-mail follows the contact's and whose level
 * is pre-selected from the type. Edit: the same fields, and the access shown
 * read-only with "ניהול גישה" (D-136).
 */
export function ContactFormModal({
  contact, organizations, fixedOrgId, defaultOrgId, canGrantAccess, canOpenAccessPage = false, forceAccess = false, headerSlot, title, onPickExisting, onClose, onSaved,
}: ContactFormModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const firstOrg = fixedOrgId || contact?.organization_id || defaultOrgId || (organizations.length === 1 ? organizations[0].id : '');
  const [form, setForm] = useState({
    organization_id: firstOrg,
    name: contact?.name || '',
    contact_role: (isContactType(contact?.contact_role) ? contact!.contact_role : '') as ContactType | '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    mobile: contact?.mobile || '',
    role_title: contact?.role_title || '',
    notes: contact?.notes || '',
    is_primary: contact?.is_primary || false,
  });
  const set = <K extends keyof typeof form>(field: K, value: (typeof form)[K]) => setForm((prev) => ({ ...prev, [field]: value }));

  const offerAccess = !contact && canGrantAccess;
  const [grantAccess, setGrantAccess] = useState(forceAccess);
  const [access, setAccess] = useState<AccessDraft>({
    email: contact?.email || '', level: defaultAccessLevel(form.contact_role), approveTickets: false, password: '',
    perms: levelDefaultPerms(defaultAccessLevel(form.contact_role)),
  });
  const levelTouched = useRef(false);
  const patchAccess = (p: Partial<AccessDraft>) => {
    if (p.level) levelTouched.current = true;
    setAccess((prev) => ({ ...prev, ...p }));
  };

  // D-135: the access e-mail follows the contact's until edited; the level
  // follows the type until chosen by hand.
  const prevEmail = useRef(form.email);
  useEffect(() => {
    // Read the ref now: the updater runs later, after the ref has moved on.
    const before = prevEmail.current;
    setAccess((prev) => ({ ...prev, email: followEmail(before, form.email, prev.email) }));
    prevEmail.current = form.email;
  }, [form.email]);
  useEffect(() => {
    if (!levelTouched.current && form.contact_role) setAccess((prev) => ({ ...prev, level: defaultAccessLevel(form.contact_role) }));
  }, [form.contact_role]);

  const chooseType = (type: ContactType) => {
    // "איש קשר ראשי" is the one who receives the reports (is_primary) — ticked
    // with the type, and still free to change.
    setForm((prev) => ({ ...prev, contact_role: type, is_primary: type === 'primary_contact' ? true : prev.is_primary }));
  };

  const accessOn = offerAccess && grantAccess;
  const blockKey = accessOn ? accessBlocker({ email: access.email, level: access.level, password: access.password }) : undefined;
  const accessError = blockKey ? t(blockKey) : undefined;

  const v = useFormValidation(CONTACT_SCHEMA, {
    'contact-name': form.name,
    'contact-type': form.contact_role,
    'contact-email': form.email,
    'contact-phone': form.phone,
    'contact-mobile': form.mobile,
  });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['contacts'] });
    queryClient.invalidateQueries({ queryKey: ['contact'] });
    queryClient.invalidateQueries({ queryKey: ['portalAccess'] });
  };
  const createMutation = useMutation({
    mutationFn: async (): Promise<{ emailSent?: boolean; res?: unknown }> => {
      const body = contactCreatePayload(form, accessOn ? { enabled: true, ...access } : null) as any;
      if (onPickExisting) body.reject_duplicate_email = true;
      if (accessOn) {
        const res: any = await api.createContactWithAccess(body);
        return { emailSent: res?.email_sent, res };
      }
      await api.createContact(body);
      return {};
    },
    onSuccess: ({ emailSent, res }) => {
      invalidate();
      if (reportScheduledMail(res)) { /* P-27: queued - "בטל" toast */ }
      else if (emailSent === true) toast('success', t('portalAccess.emailSent'));
      else if (emailSent === false) toast('warning', t('portalAccess.emailFailed'));
      onSaved?.();
      onClose();
    },
  });
  const updateMutation = useMutation({
    mutationFn: () => {
      const { organization_id: _org, ...rest } = form;
      return api.updateContact(contact!.id, { ...trimContactText(rest), contact_role: form.contact_role || undefined } as any);
    },
    onSuccess: () => { invalidate(); onSaved?.(); onClose(); },
  });
  const pending = createMutation.isPending || updateMutation.isPending;
  // D-250: the e-mail is already a contact of this customer -> offer to pick it.
  const duplicateContact = onPickExisting ? duplicateContactFromError(createMutation.error) : null;
  // D-271: the field the server's refusal is about (marked, next to the general line).
  const serverIssue = contactServerFieldIssue(contact ? updateMutation.error : createMutation.error);

  // W-08c: ONE list of everything that stops a save (customer, the contact's fields, the
  // access block). A refused click shows ALL of it in the sticky summary, marks each field
  // and focuses the first - there is no other way the click can stop.
  const problems = contactFormProblems({
    orgRequired: !contact,
    orgId: form.organization_id,
    values: { name: form.name, type: form.contact_role, email: form.email, phone: form.phone, mobile: form.mobile },
    access: accessOn ? { email: access.email, level: access.level, password: access.password } : null,
    accessPrefix: 'contact-new',
  });
  const [triedSave, setTriedSave] = useState(false);
  const shown = triedSave ? problems : [];
  /** The message on a control: the blur check's, else the refused save's. */
  const errorOn = (id: string): string | undefined => {
    if (v.errors[id]) return v.errors[id] as string;
    const p = problemOn(shown, id === 'contact-type' ? CONTACT_TYPE_FIRST_ID : id);
    return p ? t(p.key, p.params) : undefined;
  };
  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    setTriedSave(true);
    if (problems.length > 0) {
      const ids = problems.map((p) => p.id);
      // After the summary and the marks render: scroll the dialog body to the first field and focus it.
      requestAnimationFrame(() => { focusFirstProblem(ids); });
      return;
    }
    if (contact) updateMutation.mutate();
    else createMutation.mutate();
  };
  const accessEmailProblem = problemOn(shown, accessFieldId('contact-new', 'email'));
  const accessLevelProblem = problemOn(shown, accessFieldId('contact-new', 'level'));
  const accessPasswordProblem = problemOn(shown, accessFieldId('contact-new', 'password'));

  const orgSelect = !contact && !fixedOrgId && (
    <Field id={CONTACT_ORG_ID} label={t('contacts.organization')} required error={errorOn(CONTACT_ORG_ID)}>
      <EntityPicker
        id="contact-organization-id"
        icon={Building2}
        placeholder={t('contacts.selectCustomer')}
        options={organizations.map((org) => ({ value: org.id, label: org.name }))}
        value={form.organization_id}
        onChange={(v) => set('organization_id', v)}
      />
    </Field>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={title ?? (contact ? t('contacts.edit') : t('contacts.add'))}
      size="lg"
      dismissible={!pending}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary flex-1 sm:flex-none">{t('common.cancel')}</button>
          <button type="submit" form="contact-form" disabled={pending} aria-busy={pending || undefined} className="btn btn-primary flex-1 sm:flex-none">
            {pending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {contact ? t('form.save') : t('contacts.add')}
          </button>
        </>
      )}
    >
      <form id="contact-form" onSubmit={handleSubmit} noValidate className="space-y-4">
        {headerSlot}
        <SaveProblemsSummary problems={shown} />
        {orgSelect}
        <Field id="contact-name" label={t('contacts.name')} required error={errorOn('contact-name') ?? (serverIssue?.field === 'contact-name' ? serverIssue.message : undefined)}>
          <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} onBlur={() => v.blur('contact-name')} className="input" autoComplete="name" />
        </Field>

        <fieldset aria-describedby={errorOn('contact-type') ? 'contact-type-error' : undefined}>
          <legend className="label">
            {t('contacts.contactType')} <span className="text-danger-700" aria-hidden="true">*</span>
          </legend>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {CONTACT_TYPES.map((type, i) => (
              <label key={type} className="inline-flex items-center gap-2 text-sm text-surface-800 cursor-pointer">
                <input type="radio" name="contact-type" className="h-4 w-4 accent-primary-700"
                  id={i === 0 ? CONTACT_TYPE_FIRST_ID : undefined} aria-invalid={(i === 0 && !!errorOn('contact-type')) || undefined}
                  checked={form.contact_role === type} onChange={() => chooseType(type)} />
                {t(CONTACT_TYPE_LABEL_KEY[type])}
              </label>
            ))}
          </div>
          {!form.contact_role && contact && <p className="mt-1 text-xs text-surface-600">{t('contacts.typeUnclassified')}</p>}
          {errorOn('contact-type') && <p id="contact-type-error" className="error-text" role="alert">{t('contacts.typeRequired')}</p>}
        </fieldset>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field id="contact-email" label={t('contacts.email')} error={errorOn('contact-email') ?? (serverIssue?.field === 'contact-email' ? serverIssue.message : undefined)}>
            <input type="email" dir="ltr" spellCheck={false} autoComplete="email" value={form.email} onChange={(e) => set('email', e.target.value)}
              onBlur={() => v.blur('contact-email')} className="input text-start" />
          </Field>
          <Field id="contact-phone" label={t('contacts.phone')} error={errorOn('contact-phone') ?? (serverIssue?.field === 'contact-phone' ? serverIssue.message : undefined)}>
            <input type="tel" inputMode="tel" dir="ltr" autoComplete="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)}
              onBlur={() => v.blur('contact-phone')} className="input text-start" />
          </Field>
          <Field id="contact-mobile" label={t('contacts.mobile')} error={errorOn('contact-mobile') ?? (serverIssue?.field === 'contact-mobile' ? serverIssue.message : undefined)}>
            <input type="tel" inputMode="tel" dir="ltr" autoComplete="tel" value={form.mobile} onChange={(e) => set('mobile', e.target.value)}
              onBlur={() => v.blur('contact-mobile')} className="input text-start" />
          </Field>
          <Field id="contact-role-title" label={t('contacts.role')} error={serverIssue?.field === 'contact-role-title' ? serverIssue.message : undefined}>
            <input type="text" value={form.role_title} onChange={(e) => set('role_title', e.target.value)} className="input" autoComplete="organization-title" />
          </Field>
        </div>
        <Field id="contact-notes" label={t('contacts.notes')} error={serverIssue?.field === 'contact-notes' ? serverIssue.message : undefined}>
          <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} className="input min-h-20 resize-none" rows={3} />
        </Field>
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-text">
          <input type="checkbox" checked={form.is_primary} onChange={(e) => set('is_primary', e.target.checked)} className="h-4 w-4 rounded-xs accent-primary-700" />
          {t('contacts.isPrimary')}
          <span className="text-xs text-text-3">{t('contacts.isPrimaryHint')}</span>
        </label>

        {contact && <ContactAccessStatus contact={contact} canManageAccess={canOpenAccessPage} canCreateAccess={canGrantAccess && !!contact.can_edit} />}

        {offerAccess && (
          <div className="rounded-lg border border-surface-200 bg-surface-50 p-3 space-y-3">
            {!forceAccess && (
              <label className="flex items-center gap-2 text-sm font-medium text-surface-900">
                <input type="checkbox" checked={grantAccess} onChange={(e) => setGrantAccess(e.target.checked)} className="h-4 w-4 rounded-xs accent-primary-700" />
                {t('contacts.grantPortalAccess')}
              </label>
            )}
            {accessOn && <PortalAccessFields idPrefix="contact-new" value={access} onChange={patchAccess} typeDefault={form.contact_role}
              error={(triedSave ? undefined : accessError) ?? (serverIssue?.field === 'access-password' || serverIssue?.field === 'access-perms' ? serverIssue.message : undefined)}
              emailError={serverIssue?.field === 'access-email' ? serverIssue.message : (accessEmailProblem ? t(accessEmailProblem.key) : undefined)}
              levelError={accessLevelProblem ? t(accessLevelProblem.key) : undefined}
              passwordError={accessPasswordProblem ? t(accessPasswordProblem.key) : undefined} />}
          </div>
        )}

        {duplicateContact ? (
          <div role="alert" className="rounded-lg border border-warning-300 bg-warning-50 p-3 text-sm text-surface-900 space-y-2">
            {duplicateContact.hasAccess ? (
              <p>{t('portalAccess.duplicateContactHasAccess', { name: duplicateContact.name })}</p>
            ) : (
              <>
                <p>{t('portalAccess.duplicateContactEmail', { name: duplicateContact.name })}</p>
                <button type="button" className="btn btn-secondary" onClick={() => onPickExisting!({ id: duplicateContact.id, organization_id: form.organization_id })}>
                  {t('portalAccess.pickExistingContact')}
                </button>
              </>
            )}
          </div>
        ) : (
          <FormServerError error={contact ? updateMutation.error : createMutation.error} fallback={t('common.saveError')} />
        )}
      </form>
    </Dialog>
  );
}
